import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/api';
import { TimeSlot, Availability } from '../types';

interface UseLeaderboardOptions {
  roomId: string | undefined;
  minDuration: number;
  availability: Availability[]; // For triggering refetch when availability changes
}

function hashAvailability(availability: Availability[]): string {
  const availableSlots = availability
    .filter(a => a.available && a.utc_timestamp)
    .map(a => `${a.participant}:${a.utc_timestamp}`)
    .sort()
    .join('|');
  return availableSlots;
}

export function useLeaderboard({ roomId, minDuration, availability }: UseLeaderboardOptions) {
  const availabilityHash = useMemo(() => hashAvailability(availability), [availability]);
  
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['leaderboard', roomId, minDuration, availabilityHash],
    queryFn: async () => {
      if (!roomId) return { slots: [], count: 0 };
      const result = await apiService.getLeaderboard(roomId, minDuration);
      return result;
    },
    enabled: !!roomId,
    staleTime: 5000,
  });

  return {
    slots: data?.slots || [],
    count: data?.count || 0,
    isLoading,
    error,
    refetch,
  };
}

// Helper function to check if user has all hours in a slot selected
export function isSlotFullySelected(
  slot: TimeSlot,
  participantName: string,
  availability: Availability[]
): boolean {
  const selectedTimestamps = new Set<number>();
  
  availability.forEach(avail => {
    if (avail.participant === participantName && avail.available && avail.utc_timestamp) {
      const hourStart = Math.floor(avail.utc_timestamp / 3600) * 3600;
      selectedTimestamps.add(hourStart);
    }
  });

  const startTimestamp = slot.utc_start;
  const endTimestamp = slot.utc_end;
  
  for (let ts = startTimestamp; ts < endTimestamp; ts += 3600) {
    if (!selectedTimestamps.has(ts)) {
      return false;
    }
  }
  
  return true;
}

// Helper function to get missing hours that need to be selected
export function getMissingHours(
  slot: TimeSlot,
  participantName: string,
  availability: Availability[]
): Array<{ day: string; hour: number }> {
  const selectedTimestamps = new Set<number>();
  const availabilityMap = new Map<number, { day: string; hour: number }>();
  
  availability.forEach(avail => {
    if (avail.participant === participantName && avail.utc_timestamp) {
      const hourStart = Math.floor(avail.utc_timestamp / 3600) * 3600;
      if (avail.available) {
        selectedTimestamps.add(hourStart);
      }
      availabilityMap.set(hourStart, { day: avail.day, hour: avail.hour });
    }
  });

  const missingHours: Array<{ day: string; hour: number }> = [];
  const startTimestamp = slot.utc_start;
  const endTimestamp = slot.utc_end;
  
  for (let ts = startTimestamp; ts < endTimestamp; ts += 3600) {
    if (!selectedTimestamps.has(ts)) {
      const existing = availabilityMap.get(ts);
      if (existing) {
        missingHours.push(existing);
      } else {
        const date = new Date(ts * 1000);
        const localDay = date.toLocaleDateString('en-CA');
        const localHour = date.getHours();
        missingHours.push({ day: localDay, hour: localHour });
      }
    }
  }
  
  return missingHours;
}

